<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { debounce } from '@chatwoot/utils';

import { useDocumentsStore } from 'dashboard/stores/documents';
import { useDocumentTypesStore } from 'dashboard/stores/documentTypes';
import {
  DOCUMENT_STATUS_KEYS,
  documentStatusLabel,
} from 'dashboard/components-next/CRM/Documents/constants';
import DocumentsListLayout from 'dashboard/components-next/CRM/Documents/DocumentsListLayout/DocumentsListLayout.vue';
import DocumentCard from 'dashboard/components-next/CRM/Documents/DocumentCard/DocumentCard.vue';
import DocumentsEmptyState from 'dashboard/components-next/CRM/Documents/DocumentsEmptyState/DocumentsEmptyState.vue';
import CreateDocumentDialog from 'dashboard/components-next/CRM/Documents/CreateDocumentDialog/CreateDocumentDialog.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const DEBOUNCE_DELAY = 300;
const ITEMS_PER_PAGE = 25;

const store = useDocumentsStore();
const documentTypesStore = useDocumentTypesStore();
const route = useRoute();
const router = useRouter();
const { t, te } = useI18n();

const createDialogRef = ref(null);
const archiveDialogRef = ref(null);
const pendingArchive = ref(null);
const versionFileInput = ref(null);
const uploadTarget = ref(null);

const documents = computed(() => store.getDocuments);
const summary = computed(() => store.getSummary);
const meta = computed(() => store.getMeta);
const uiFlags = computed(() => store.getUIFlags);

const searchQuery = computed(() => route.query?.search || '');
const activeFilter = computed(() => route.query?.filter || 'all');
const pageNumber = computed(() => Number(route.query?.page) || 1);
const searchValue = ref(searchQuery.value);

const hasDocuments = computed(() => documents.value.length > 0);
const isFetchingList = computed(() => uiFlags.value.fetchingList);

const showEmptyState = computed(
  () =>
    !isFetchingList.value &&
    !uiFlags.value.listError &&
    !searchQuery.value &&
    activeFilter.value === 'all' &&
    !hasDocuments.value
);
const showEmptyText = computed(
  () =>
    !isFetchingList.value &&
    !uiFlags.value.listError &&
    !showEmptyState.value &&
    !hasDocuments.value
);

// Built-in lifecycle states plus any custom states declared by active types.
const filterStatuses = computed(() => {
  const customStates = documentTypesStore.getActiveDocumentTypes.flatMap(
    type => type.lifecycle?.states || []
  );
  return [...new Set([...DOCUMENT_STATUS_KEYS, ...customStates])];
});

const filterOptions = computed(() => [
  { value: 'all', label: `${t('DOCUMENTS.FILTERS.ALL')} (${summary.value.total || 0})` },
  {
    value: 'needs_action',
    label: `${t('DOCUMENTS.FILTERS.NEEDS_ACTION')} (${summary.value.needsAction || 0})`,
  },
  {
    value: 'overdue',
    label: `${t('DOCUMENTS.FILTERS.OVERDUE')} (${summary.value.overdue || 0})`,
  },
  ...filterStatuses.value.map(status => ({
    value: `status:${status}`,
    label: documentStatusLabel(status, t, te),
  })),
]);

const filterParams = filter => {
  if (filter === 'all') return {};
  if (filter.startsWith('status:')) return { status: filter.slice(7) };
  return { bucket: filter };
};

const updateURLParams = ({
  page = pageNumber.value,
  search = searchQuery.value,
  filter = activeFilter.value,
}) => {
  router.replace({
    query: {
      ...route.query,
      page: page > 1 ? String(page) : undefined,
      search: search || undefined,
      filter: filter === 'all' ? undefined : filter,
    },
  });
};

const fetchDocuments = () =>
  store.get({
    page: pageNumber.value,
    search: searchQuery.value || undefined,
    ...filterParams(activeFilter.value),
  });

// Single fetch path: state lives in the URL so back/forward restores the view.
watch(
  () => [route.query.search, route.query.filter, route.query.page],
  () => {
    searchValue.value = searchQuery.value;
    fetchDocuments();
  },
  { immediate: true }
);

const onSearch = debounce(query => {
  searchValue.value = query;
  updateURLParams({ page: 1, search: query });
}, DEBOUNCE_DELAY);

const onFilterChange = filter => updateURLParams({ page: 1, filter });
const onPageChange = page => updateURLParams({ page });

const handleAddDocument = () => createDialogRef.value?.open();

const handleManageTypes = () => {
  router.push({
    name: 'crm_document_types',
    params: { accountId: route.params.accountId },
  });
};

const handleCreateDocument = async payload => {
  try {
    await store.create(payload);
    createDialogRef.value?.close();
    useAlert(t('DOCUMENTS.CREATE.SUCCESS'));
    fetchDocuments();
  } catch (error) {
    useAlert(t('DOCUMENTS.CREATE.ERROR'));
  }
};

const handleShowDocument = documentId => {
  router.push({
    name: 'crm_documents_show',
    params: { accountId: route.params.accountId, documentId },
  });
};

const runTransition = async (document, status) => {
  try {
    await store.transition(document.id, status);
    useAlert(t('DOCUMENTS.TRANSITION_SUCCESS'));
    fetchDocuments();
  } catch (error) {
    useAlert(t('DOCUMENTS.TRANSITION_ERROR'));
  }
};

const handleTransition = ({ document, status }) => {
  if (status === 'archived') {
    pendingArchive.value = { document, status };
    archiveDialogRef.value?.open();
    return;
  }
  runTransition(document, status);
};

const confirmArchive = () => {
  if (pendingArchive.value) {
    runTransition(pendingArchive.value.document, pendingArchive.value.status);
  }
  pendingArchive.value = null;
};

const chooseVersionFile = document => {
  uploadTarget.value = document;
  if (versionFileInput.value) {
    versionFileInput.value.value = '';
    versionFileInput.value.click();
  }
};

const uploadVersion = async event => {
  const [file] = event.target.files || [];
  if (!file || !uploadTarget.value) return;

  const payload = new FormData();
  payload.append('file', file);
  try {
    await store.createVersion(uploadTarget.value.id, payload);
    useAlert(t('DOCUMENTS.UPLOAD_SUCCESS'));
    fetchDocuments();
  } catch (error) {
    useAlert(t('DOCUMENTS.UPLOAD_ERROR'));
  } finally {
    uploadTarget.value = null;
  }
};

const loadDocumentTypes = async () => {
  try {
    await documentTypesStore.get();
  } catch {
    // Filters simply fall back to the built-in lifecycle states.
  }
};

onMounted(loadDocumentTypes);

const handleDownload = async document => {
  try {
    await store.download(document.id, {
      filename: document.currentVersion?.filename || document.title,
    });
  } catch (error) {
    useAlert(t('DOCUMENTS.DOWNLOAD_ERROR'));
  }
};
</script>

<template>
  <div class="flex flex-col justify-between flex-1 h-full m-0 overflow-auto bg-n-background">
    <DocumentsListLayout
      :search-value="searchValue"
      :header-title="$t('DOCUMENTS.TITLE')"
      :filter-options="filterOptions"
      :active-filter="activeFilter"
      :current-page="pageNumber"
      :total-items="Number(meta.totalCount)"
      :items-per-page="ITEMS_PER_PAGE"
      show-manage-types
      @page-change="onPageChange"
      @search="onSearch"
      @add="handleAddDocument"
      @manage-types="handleManageTypes"
      @update:filter="onFilterChange"
    >
      <template #empty>
        <div v-if="isFetchingList" class="flex items-center justify-center py-10 text-n-slate-11">
          <Spinner />
        </div>

        <div
          v-else-if="uiFlags.listError"
          class="flex flex-col items-center justify-center gap-3 py-10"
        >
          <span class="text-base text-n-slate-11">
            {{ t('DOCUMENTS.ERROR.TITLE') }}
          </span>
          <Button :label="t('DOCUMENTS.ERROR.RETRY')" size="sm" slate @click="fetchDocuments" />
        </div>

        <DocumentsEmptyState v-else-if="showEmptyState" class="pt-14" @create="handleAddDocument" />

        <div v-else-if="showEmptyText" class="flex items-center justify-center py-10">
          <span class="text-base text-n-slate-11">
            {{ t('DOCUMENTS.NO_MATCHES') }}
          </span>
        </div>
      </template>

      <div v-if="!isFetchingList && hasDocuments" class="flex flex-col gap-4 pt-4 pb-6">
        <DocumentCard
          v-for="document in documents"
          :key="document.id"
          :document="document"
          :is-transitioning="store.isTransitioning(document.id)"
          :is-uploading="store.isUploading(document.id)"
          :is-downloading="store.isDownloading(document.id)"
          @show-document="handleShowDocument"
          @transition="handleTransition"
          @upload="chooseVersionFile"
          @download="handleDownload"
        />
      </div>
    </DocumentsListLayout>

    <CreateDocumentDialog
      ref="createDialogRef"
      :is-submitting="uiFlags.creating"
      @create="handleCreateDocument"
    />
    <Dialog
      ref="archiveDialogRef"
      type="alert"
      :title="t('DOCUMENTS.CONFIRM_ARCHIVE.TITLE')"
      :description="t('DOCUMENTS.CONFIRM_ARCHIVE.DESCRIPTION')"
      :confirm-button-label="t('DOCUMENTS.CONFIRM_ARCHIVE.CONFIRM')"
      @confirm="confirmArchive"
    />
    <input
      ref="versionFileInput"
      type="file"
      accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
      class="hidden"
      @change="uploadVersion"
    />
  </div>
</template>
